import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, ExternalLink, Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MembershipTiers } from "@/components/membership/MembershipTiers";
import { MemberLinkBox } from "@/components/membership/MemberLinkBox";

export default function Membership() {
  const [params] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [tier, setTier] = useState(params.get("tier") === "care" ? "care" : "care_plus");
  const ref = (params.get("ref") || "").toUpperCase();
  const [referrer, setReferrer] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/memberships/tiers").then((r) => setTiers(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (ref) api.get(`/memberships/referral/${ref}`).then((r) => setReferrer(r.data)).catch(() => setReferrer(null)); }, [ref]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ok = form.name.trim().length > 1 && /\S+@\S+\.\S+/.test(form.email);
  const chosen = tiers.find((t) => t.id === tier);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/memberships", { tier, ...form, ref: referrer ? referrer.code : "" }, { timeout: 60000 });
      window.location.href = r.data.checkout_url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't start checkout — please call 310-422-0669"); setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="membership-page">
      <div className="mb-8 dg-reveal">
        <span className="dg-chip"><ShieldCheck className="w-3 h-3" /> Membership · cancel anytime</span>
        {referrer && <span className="dg-chip ml-2 text-[#FFD600] border-[#FFD600]/50" data-testid="membership-referred-chip"><Gift className="w-3 h-3" /> Invited by {referrer.first_name} — they get a free month when you join</span>}
        <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-display">David on call. Every month.</h1>
        <p className="mt-3 text-slate-300 max-w-2xl">Skip the line when something breaks. Members get priority answers from Ask Dr.Gadget, 10% off every session and install — and Care+ includes a 30-minute session each month. Billed monthly by card through Stripe, cancel whenever you like.</p>
      </div>

      <form onSubmit={submit} className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="dg-eyebrow mb-3">1 · Pick your plan</div>
          <MembershipTiers tiers={tiers} selected={tier} onSelect={setTier} />
        </div>
        <div className="lg:col-span-5 grid gap-4">
          <div className="dg-eyebrow">2 · Your details</div>
          <label className="block"><div className="dg-eyebrow mb-1">Full name</div><input className="dg-input" value={form.name} onChange={set("name")} data-testid="membership-name" autoComplete="name" /></label>
          <label className="block"><div className="dg-eyebrow mb-1">Email — use this same email when you book or ask</div><input className="dg-input" type="email" value={form.email} onChange={set("email")} data-testid="membership-email" autoComplete="email" /></label>
          <label className="block"><div className="dg-eyebrow mb-1">Phone (optional)</div><input className="dg-input" type="tel" value={form.phone} onChange={set("phone")} data-testid="membership-phone" autoComplete="tel" /></label>
          <button type="submit" disabled={!ok || busy || !chosen} className="dg-btn-primary rounded-full px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-40" data-testid="membership-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            {busy ? "Opening secure checkout…" : chosen ? `Join ${chosen.name} — $${Number(chosen.price_usd).toFixed(0)}/month` : "Join"}
          </button>
          <p className="text-xs text-slate-500">Secure Stripe checkout. Your card is charged ${chosen ? Number(chosen.price_usd).toFixed(2) : "…"} today and then monthly. Cancel anytime from your membership page — no refunds for a partial month. Perks apply automatically to your email; no codes. Already a member? Use the box below to get your membership link, or <a href="tel:+13104220669" className="dg-link">call 310-422-0669</a>.</p>
          <p className="text-xs text-slate-500">Prefer one-off help? <Link to="/book" className="dg-link">Book a single session</Link>.</p>
        </div>
      </form>
      <MemberLinkBox />
    </div>
  );
}
